import { useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { FilterToggleButton } from '@/components/common/FilterToggleButton/FilterToggleButton'
import { useActiveFiltersCount } from '@/hooks/useActiveFilterCount/useActiveFilterCount'
import {
    DEFAULT_ACTIVE_MANAGEMENT_FILTERING,
    ActiveManagementFiltersValues,
} from '@/utils/constants'
import { ActiveFiltersCounter } from '@/components/common/ActiveFiltersCounter/ActiveFiltersCounter'
import {
    createSelectHasDefaultFilters,
    FiltersStoreState,
} from '@/stores/createFiltersStore'
import { TimelineFilterValue } from '@/components/common/Filter/types'
import { NodeFilter } from '@/components/core/filters/NodeFilter'
import { TagFilter } from '@/components/core/filters/TagFilter'
import { FacilityFilter } from '@/components/core/filters/FacilityFilter'
import { TimelineFilter } from '@/components/core/filters/TimelineFilter'
import { MeterFilter } from '@/components/core/filters/MeterFilter'
import { Tooltip } from '@/components/atoms/Tooltip/Tooltip'
import { SubmeterFilter } from '@/components/core/filters/SubmeterFilter'
import { LinkButton } from '@/components'
import { USNFilter } from '@/components/core/filters/USNFilter'
import { useActiveManagementFiltersStore } from '../../stores/useActiveManagementFiltersStore'
import { useActiveManagementFiltersGetParams } from '../../hooks/useActiveManagementFiltersGetParams'
import useActiveManagementStore from '../../stores/useActiveManagementStore/useActiveManagementStore'
import classnames from 'classnames'

type ActiveManagementFiltersContainerProps = {
    activeFilters: ActiveManagementFiltersValues
    onChange: FiltersStoreState<ActiveManagementFiltersValues>['updateActiveFilter']
    handleResetFilters: () => void
    showResetFiltersButton: boolean
    selectedFilter?: keyof ActiveManagementFiltersValues
}

export const ActiveManagementFiltersContainer: React.FC<
    ActiveManagementFiltersContainerProps
> = (props) => {
    const {
        activeFilters,
        onChange,
        handleResetFilters,
        showResetFiltersButton,
        selectedFilter,
    } = props
    const { t } = useTranslation('translations')
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(true)
    const activeFiltersCount = useActiveFiltersCount(
        activeFilters,
        DEFAULT_ACTIVE_MANAGEMENT_FILTERING
    )

    const hoveredDisplaySetting = useActiveManagementStore(
        (state) => state.intervalData.hoveredDisplaySetting
    )

    const isMeterFilterDisabled = selectedFilter === 'subMeter'
    const isSubmeterFilterDisabled = selectedFilter === 'meter'

    const getMeterFilterHighlightClass = useCallback(() => {
        const isHovered = hoveredDisplaySetting === 'meters'

        return classnames({
            'outline outline-4 outline-blue-200': isHovered,
            '!shadow-inset-azure !border-azure': isSubmeterFilterDisabled,
        })
    }, [hoveredDisplaySetting, isSubmeterFilterDisabled])

    const getSubmeterFilterHighlightClass = useCallback(() => {
        const isHovered = hoveredDisplaySetting === 'subMeters'

        return classnames({
            'outline outline-4 outline-blue-200': isHovered,
            '!shadow-inset-azure !border-azure': isMeterFilterDisabled,
        })
    }, [hoveredDisplaySetting, isMeterFilterDisabled])

    const handleToggleVisibility = () => {
        setIsVisible((prevVisible) => !prevVisible)
    }

    const params = useActiveManagementFiltersGetParams(activeFilters)
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-4" ref={containerRef}>
                <div
                    className="flex items-center justify-between gap-2"
                    data-testid="active-management-filters-title-row"
                >
                    <div className="text-heading-6 py-2.5">
                        {t('filters.title')}
                    </div>
                    {!isVisible && (
                        <ActiveFiltersCounter count={activeFiltersCount} />
                    )}
                </div>
                {isVisible && (
                    <>
                        <NodeFilter
                            name="active-management-node-filter"
                            value={activeFilters.node.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ACTIVE_MANAGEMENT_FILTERING.node.map(
                                (id) => id.toString()
                            )}
                            containerRef={containerRef}
                            onChange={(value: string[]) => {
                                onChange(
                                    'node',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                        />
                        <TagFilter
                            name="active-management-tag-filter"
                            containerRef={containerRef}
                            value={activeFilters.tag.map((id) => id.toString())}
                            defaultValue={DEFAULT_ACTIVE_MANAGEMENT_FILTERING.tag.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'tag',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                            }}
                        />
                        <FacilityFilter
                            name="active-management-facility-filter"
                            containerRef={containerRef}
                            value={activeFilters.facility.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ACTIVE_MANAGEMENT_FILTERING.facility.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'facility',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'tagIds[]': params['tagIds[]'],
                            }}
                        />
                        <USNFilter
                            name="active-management-usn-filter"
                            containerRef={containerRef}
                            value={(activeFilters.usn ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_ACTIVE_MANAGEMENT_FILTERING.usn ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'usn',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                            }}
                        />
                        <Tooltip
                            name="meter-filter-disabled-tooltip"
                            content={t('filters.meter.disabledTooltip')}
                            show={isMeterFilterDisabled}
                            placement="bottom"
                            arrowPlacement="top"
                        >
                            <MeterFilter
                                name="active-management-meter-filter"
                                containerRef={containerRef}
                                value={activeFilters.meter.map((id) =>
                                    id.toString()
                                )}
                                defaultValue={DEFAULT_ACTIVE_MANAGEMENT_FILTERING.meter.map(
                                    (id) => id.toString()
                                )}
                                onChange={(value: string[]) => {
                                    onChange(
                                        'meter',
                                        value.map((id) => parseInt(id))
                                    )
                                }}
                                className={getMeterFilterHighlightClass()}
                                disabled={isMeterFilterDisabled}
                                params={{
                                    'nodeIds[]': params['nodeIds[]'],
                                    'tagIds[]': params['tagIds[]'],
                                    'facilityIds[]': params['facilityIds[]'],
                                    'usnAccountIds[]':
                                        params['usnAccountIds[]'],
                                    reportType: 'interval',
                                }}
                            />
                        </Tooltip>
                        <Tooltip
                            name="subMeter-filter-disabled-tooltip"
                            content={t('filters.subMeter.disabledTooltip')}
                            show={isSubmeterFilterDisabled}
                            placement="bottom"
                            arrowPlacement="top"
                        >
                            <SubmeterFilter
                                name="active-management-subMeter-filter"
                                containerRef={containerRef}
                                value={(activeFilters.subMeter ?? []).map(
                                    (id) => id.toString()
                                )}
                                defaultValue={(
                                    DEFAULT_ACTIVE_MANAGEMENT_FILTERING.subMeter ??
                                    []
                                ).map((id) => id.toString())}
                                onChange={(value: string[]) => {
                                    onChange(
                                        'subMeter',
                                        value.map((id) => parseInt(id))
                                    )
                                }}
                                className={getSubmeterFilterHighlightClass()}
                                disabled={isSubmeterFilterDisabled}
                                params={{
                                    'nodeIds[]': params['nodeIds[]'],
                                    'tagIds[]': params['tagIds[]'],
                                    'facilityIds[]': params['facilityIds[]'],
                                    'usnAccountIds[]':
                                        params['usnAccountIds[]'],
                                }}
                            />
                        </Tooltip>
                        <TimelineFilter
                            name="active-management-timeline-filter"
                            value={activeFilters.timeline}
                            defaultValue={
                                DEFAULT_ACTIVE_MANAGEMENT_FILTERING.timeline
                            }
                            onChange={(value: TimelineFilterValue) => {
                                onChange('timeline', value)
                            }}
                            containerRef={containerRef}
                        />
                    </>
                )}
                {showResetFiltersButton && (
                    <LinkButton
                        name="reset-filters"
                        iconBefore="sync"
                        label={t('filters.reset')}
                        onClick={handleResetFilters}
                    />
                )}
                <FilterToggleButton
                    isVisible={isVisible}
                    onToggle={handleToggleVisibility}
                />
            </div>
        </div>
    )
}

type ActiveManagementFiltersProps = {
    selectedFilter?: keyof ActiveManagementFiltersValues
}

export const ActiveManagementFilters: React.FC<ActiveManagementFiltersProps> = (
    props
) => {
    const { selectedFilter } = props
    const updateActiveFilter = useActiveManagementFiltersStore(
        (state) => state.updateActiveFilter
    )

    const activeFilters = useActiveManagementFiltersStore(
        (state) => state.activeFilters
    )

    const selectHasDefaultFilters = createSelectHasDefaultFilters(
        DEFAULT_ACTIVE_MANAGEMENT_FILTERING
    )

    const storeShowResetFiltersButton = !useActiveManagementFiltersStore(
        selectHasDefaultFilters
    )

    const handleResetActiveFilters = useActiveManagementFiltersStore(
        (state) => state.resetActiveFilters
    )

    return (
        <div className="relative z-30 border-b border-neutral-200 px-6 pb-6">
            <ActiveManagementFiltersContainer
                onChange={updateActiveFilter}
                activeFilters={activeFilters}
                handleResetFilters={handleResetActiveFilters}
                showResetFiltersButton={storeShowResetFiltersButton}
                selectedFilter={selectedFilter}
            />
        </div>
    )
}
